import { CompanySettings } from './company-settings';
import { User } from './user';

export class Company {
  organisation_registration_number!: string;
  organisation_name?: string;
  organisation_type?: string;
  status!: string;
  country?: string;
  town?: string;
  province?: string;
  street_address?: string;
  tel_number?: string;
  mobile_number?: string;
  fax_number?: string;
  website?: string;
  primary_contact_title?: string;
  primary_contact_first_name?: string;
  primary_contact_middle_name?: string;
  primary_contact_last_name?: string;
  primary_contact_position?: string;
  primary_contact_gender?: string;
  primary_contact_email_address?: string;
  primary_contact_mobile_number?: string;
  company_profile_upload_path?: string;
  has_praz_registration?: boolean;
  praz_registration_number?: string;
  praz_certificate_upload_path?: string;
  praz_certificate_expiry_date?: number;
  tax_clearance_certificate_upload_path?: string;
  tax_clearance_expiry_date?: number;
  logo_upload_path?: string;
  certificate_of_incorporation_upload_path?: string;
  cr6_upload_path?: string;
  cr5_upload_path?: string;
  cr14_upload_path?: string;
  primary_color!: string;
  ratings_count?: number;
  ratings_value?: number;
  date_approved?: number;
  uuid!: string;
  date_created!: number;
  date_updated!: number;

  user?: User;
  company_settings?: CompanySettings;

  constructor(data: any) {
    this.organisation_registration_number = data.organisation_registration_number;
    this.organisation_name = data.organisation_name;
    this.organisation_type = data.organisation_type;
    this.status = data.status;
    this.country = data.country;
    this.town = data.town;
    this.province = data.province;
    this.street_address = data.street_address;
    this.tel_number = data.tel_number;
    this.mobile_number = data.mobile_number;
    this.fax_number = data.fax_number;
    this.website = data.website;
    this.primary_contact_title = data.primary_contact_title;
    this.primary_contact_first_name = data.primary_contact_first_name;
    this.primary_contact_middle_name = data.primary_contact_middle_name;
    this.primary_contact_last_name = data.primary_contact_last_name;
    this.primary_contact_position = data.primary_contact_position;
    this.primary_contact_gender = data.primary_contact_gender;
    this.primary_contact_email_address = data.primary_contact_email_address;
    this.primary_contact_mobile_number = data.primary_contact_mobile_number;
    this.company_profile_upload_path = data.company_profile_upload_path;
    this.has_praz_registration = data.has_praz_registration;
    this.praz_registration_number = data.praz_registration_number;
    this.praz_certificate_upload_path = data.praz_certificate_upload_path;
    this.praz_certificate_expiry_date = data.praz_certificate_expiry_date;
    this.tax_clearance_certificate_upload_path = data.tax_clearance_certificate_upload_path;
    this.tax_clearance_expiry_date = data.tax_clearance_expiry_date;
    this.logo_upload_path = data.logo_upload_path;
    this.certificate_of_incorporation_upload_path = data.certificate_of_incorporation_upload_path;
    this.cr6_upload_path = data.cr6_upload_path;
    this.cr5_upload_path = data.cr5_upload_path;
    this.cr14_upload_path = data.cr14_upload_path;
    this.primary_color = data.primary_color;
    this.ratings_count = data.ratings_count;
    this.ratings_value = data.ratings_value;
    this.date_approved = data.date_approved;
    this.uuid = data.uuid;
    this.date_created = data.date_created;
    this.date_updated = data.date_updated;

    if (data.user) {
      this.user = new User(data.user);
    }
    if (data.company_settings) {
      this.company_settings = new CompanySettings(data.company_settings);
    }
  }

  private filled(v: any): boolean {
    return v !== null && v !== undefined && String(v).trim() !== '';
  }

  /** Initials for the avatar placeholder, e.g. "Mutare Dry Port" -> "MDP". */
  getInitials(): string {
    const name = (this.organisation_name || '').trim();
    if (!name) return '--';
    return name.split(/\s+/).filter(Boolean).slice(0, 3).map((p) => p[0]).join('').toUpperCase();
  }

  /** Overall profile completion (%) across the key company fields. */
  getProfileCompletion(): number {
    const fields = [
      this.organisation_name, this.organisation_registration_number, this.organisation_type, this.status,
      this.street_address, this.town, this.province, this.country,
      this.tel_number, this.mobile_number, this.website,
      this.primary_contact_first_name, this.primary_contact_position,
      this.primary_contact_email_address, this.primary_contact_mobile_number,
      this.primary_color, this.logo_upload_path, this.certificate_of_incorporation_upload_path,
    ];
    const done = fields.filter((f) => this.filled(f)).length;
    return Math.round((done / fields.length) * 100);
  }

  /** Whether a profile section is complete (drives the Account Health checklist). */
  sectionComplete(section: 'organisation' | 'contact' | 'branding' | 'compliance'): boolean {
    switch (section) {
      case 'organisation':
        return this.filled(this.organisation_name) && this.filled(this.organisation_registration_number)
          && this.filled(this.organisation_type) && this.filled(this.status);
      case 'contact':
        return (this.filled(this.tel_number) || this.filled(this.mobile_number)) && this.filled(this.primary_contact_email_address);
      case 'branding':
        return this.filled(this.logo_upload_path) && this.filled(this.primary_color);
      case 'compliance':
        return !!this.has_praz_registration
          && (this.filled(this.certificate_of_incorporation_upload_path) || this.filled(this.tax_clearance_certificate_upload_path));
      default:
        return false;
    }
  }

  /** Recent-activity timeline derived from real lifecycle timestamps (no fabrication). */
  getRecentActivity(): { label: string; date?: number; icon: string }[] {
    const items: { label: string; date?: number; icon: string }[] = [];
    if (this.date_created) items.push({ label: 'Company profile created', date: this.date_created, icon: 'bi-building-add' });
    if (this.date_approved) items.push({ label: 'Account approved', date: this.date_approved, icon: 'bi-patch-check' });
    if (this.date_updated && this.date_updated !== this.date_created) {
      items.push({ label: 'Details last updated', date: this.date_updated, icon: 'bi-pencil-square' });
    }
    return items;
  }
}
